import { useCallback, useEffect, useMemo, useState } from 'react'
import { FetchState } from 'shared/api'
import { flatten, maxText } from 'shared/util'
import { useKeyBind } from 'shared/keyBind'

import { ResearchProjectResult, useGetResearchProject } from 'interface/research'

import { useSearch, useTargetList } from 'features/search/context'
import { useSisetuPreviewDialog } from 'features/sisetu/dialog/preview/hooks'
import { useKojinPreviewDialog } from 'features/kojin/dialog/preview/hooks'
import { SisetuResult } from 'interface/sisetu'
import { KojinResult } from 'interface/kojin'

type useResearchPreviewProps = {
  projectList?: ResearchProjectResult[]
  defaultProjectKey?: string | null
	defaultProject?: ResearchProjectResult | null
  targetSisetuCode?: string
  targetKojinCode?: string
	onSisetuOpenReply?: (sisetuCode: string | null) => void
  onKojinOpenReply?: (kojinCode: string | null) => void
  onClose?: () => void
}
export const useResearchPreview = ({
	projectList,
	defaultProjectKey,
	defaultProject,
  targetSisetuCode,
  targetKojinCode,
	onSisetuOpenReply,
	onKojinOpenReply,
  onClose
}: useResearchPreviewProps) => {
	const search = useSearch()
	const targetList = useTargetList()

	const [projectKey, setProjectKey] = useState<string | null>(defaultProjectKey ?? defaultProject?.projectKey ?? null)
	const [currentBunkenNo, setCurrentBunkenNo] = useState<string | null>(null)

	const {
		current,
		prev,
		next,
	} = useMemo(
		() => {
			const index = (
				projectKey ? (projectList?.findIndex(research => research.projectKey === projectKey) ?? -1) :
				-1
			)
			return {
				current: (projectList && index >= 0) ? projectList[index] : null,
				next: (projectList && index >= 0 && index < (projectList.length - 1)) ? projectList[index + 1] : null,
				prev: (projectList && index > 0) ? projectList[index - 1] : null
			}
		},
		[projectList, projectKey]
	)

	const projectState: FetchState<ResearchProjectResult> = useGetResearchProject(
		search?.searchId,
		projectKey,
		{
			defaultProject: current ?? defaultProject ?? undefined,
			updateTime: maxText(targetList?.updateTime, search?.updateTime) ?? undefined
		}
	)
  const project = useMemo(() => projectState.data, [projectState])

	useEffect(
		() => {
			setCurrentBunkenNo(
				project ? (
					project.researchs.length > 0 ? project.researchs[0].bunkenNo : null
				) : null
			)
		},
		[project]
	)

	const researchs = useMemo(
		() => {
			const researchs = project?.researchs ?? []
			researchs.sort((a, b) => {
				if (b.nendo === a.nendo) {
					return b.bunkenNo.localeCompare(a.bunkenNo)
				}
				return b.nendo - a.nendo
			})
			return researchs
		},
		[project]
	)

	const currentResearch = useMemo(
		() => (
			researchs.length == 0 ? null :
			currentBunkenNo ? researchs.find(r => r.bunkenNo === currentBunkenNo) ?? null : null
		),
		[researchs, currentBunkenNo]
	)

	const {
		setSisetuList, sisetuList,
		openSisetu, sisetuCode: selectedSisetuCode
	} = useSisetuPreviewDialog()
	const {
		setKojinList, kojinList,
		openKojin, kojinCode: selectedKojinCode
	} = useKojinPreviewDialog()

	const isShowModal = useMemo(
		() => !!(selectedSisetuCode || selectedKojinCode),
		[selectedSisetuCode, selectedKojinCode]
	)

	const openPrev = useCallback(
		() => {
			if (isShowModal) return
			if (prev?.projectKey) setProjectKey(prev.projectKey)
		},
		[prev, setProjectKey, isShowModal]
	)
	const openNext = useCallback(
		() => {
			if (isShowModal) return
			if (next?.projectKey) setProjectKey(next.projectKey)
		},
		[next, setProjectKey, isShowModal]
	)

	const closeModal = useCallback(
		(exclusive: 'sisetu' | 'kojin' | null = null) => {
			if (exclusive !== 'sisetu') {
				if (onSisetuOpenReply) {
					onSisetuOpenReply(null)
				} else {
					openSisetu(null)
				}
			}
			if (exclusive !== 'kojin') {
				if (onKojinOpenReply) {
					onKojinOpenReply(null)
				} else {
					openKojin(null)
				}
			}
		},
		[onSisetuOpenReply, openSisetu, onKojinOpenReply, openKojin]
	)

	const onSisetuOpen = useCallback(
		(sisetuCode: string | null, list?: SisetuResult[]) => {
			closeModal('sisetu')
			if (onSisetuOpenReply) {
				onSisetuOpenReply(sisetuCode)
			} else {
				setSisetuList(list ?? [])
				openSisetu(sisetuCode)
			}
		},
		[closeModal, onSisetuOpenReply, openSisetu, setSisetuList]
	)

	const onKojinOpen = useCallback(
		(kojinCode: string | null, list?: KojinResult[]) => {
			closeModal('kojin')
			if (onKojinOpenReply) {
				onKojinOpenReply(kojinCode)
			} else {
				setKojinList(list ?? [])
				openKojin(kojinCode)
			}
		},
		[closeModal, onKojinOpenReply, openKojin, setKojinList]
	)

  useKeyBind({ key: "PageUp", onKeyDown: openPrev })
  useKeyBind({ key: "PageDown", onKeyDown: openNext })

	return {
		loading: projectState.loading,
		error: flatten([projectState.error]),
		projectList, projectKey, project,
		researchs, currentResearch, currentBunkenNo, setCurrentBunkenNo,
		targetSisetuCode,
		targetKojinCode,
		openPrev, hasPrev: prev !== null ? true : false,
		openNext, hasNext: next !== null ? true : false,
		onSisetuOpen, selectedSisetuCode, sisetuList,
		onKojinOpen, selectedKojinCode, kojinList,
		closeModal,
		onClose
	}
}
